/**
 * Life4Earth V2 — React hook for loading KPI configs from the API.
 *
 * Replaces: localStorage key l4e_project_kpi_config_overrides_v1
 *           from Data/ProjectConfig/projectKpiConfigOverrides.ts
 *
 * Returns KPI configs grouped by project, converted to the SubKpiInstance
 * shape the kpi-engine expects. Falls back to localStorage when the API
 * is unreachable or returns empty data for a project.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllKpiConfigs,
  groupKpiConfigsByProject,
} from "./kpiConfigs";
import type { KpiConfigResponse, GroupedKpiConfigs } from "./kpiConfigs";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

export interface UseApiKpiConfigsResult {
  /**
   * Map<projectGuid, { schedule, finance, mission: SubKpiInstance[] }>
   * SubKpiInstance in the seed-data shape the kpi-engine expects.
   */
  configsByProject: Map<string, GroupedKpiConfigs>;
  /** Raw API responses */
  raw: KpiConfigResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "none" | "loading";
  refetch: () => void;
}

export function useApiKpiConfigs(): UseApiKpiConfigsResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiKpiConfigsResult>({
    configsByProject: new Map(),
    raw: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const cached = await fetchForKey(
        QUERY_KEYS.kpiConfigs,
        async () => {
          const apiConfigs = await fetchAllKpiConfigs();
          return {
            raw: apiConfigs,
            transformed: groupKpiConfigsByProject(apiConfigs),
          };
        },
        { force: true }
      );

      if (mountedRef.current) {
        setData({
          configsByProject: cached.transformed,
          raw: cached.raw,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          configsByProject: new Map(),
          raw: [],
          loading: false,
          error: err.message,
          source: "none",
          refetch,
        });
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    async function load() {
      try {
        const cached = await fetchForKey(
          QUERY_KEYS.kpiConfigs,
          async () => {
            const apiConfigs = await fetchAllKpiConfigs();
            return {
              raw: apiConfigs,
              transformed: groupKpiConfigsByProject(apiConfigs),
            };
          }
        );

        if (mountedRef.current) {
          setData({
            configsByProject: cached.transformed,
            raw: cached.raw,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            configsByProject: new Map(),
            raw: [],
            loading: false,
            error: err.message,
            source: "none",
            refetch,
          });
        }
      }
    }

    load();

    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === QUERY_KEYS.kpiConfigs) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [refetch]);

  return data;
}
